<script setup>
import UserType from '@/components/UserType.vue'
import AppStatus from '@/components/AppStatus.vue'
import AdminNavigation from '@/components/AdminNavigation.vue'
import HiveLoadingIndicator from '@/components/HiveLoadingIndicator.vue'
import GIEPLogo from '@/components/GIEPLogo.vue'
import GuestNavigation from '@/components/GuestBottomNavigation.vue'
import ProfessionalNavigation from '@/components/ProfessionalNavigation.vue'
import TraineeNavigation from '@/components/TraineeNavigation.vue'

import {useAuth} from "@/stores/auth";

const authStore = useAuth()

import {useConnectivityCheck} from "@/composables/useConnectivityCheck";
import {computed, ref} from "vue";

const {reloadKey} = useConnectivityCheck()

const appStatus = ref(null)

const main_classes = computed(() => {
    let classes = 'flex flex-column h-full'

    if (authStore.authUser) {
        classes += ' is_authenticated'
        if (authStore.authUser.isSuperAdmin) {
            classes += ' is_superadmin'
        }
        else if (authStore.authUser.isAdmin) {
            classes += ' is_admin'
        }
        else if (authStore.authUser.isProfessional) {
            classes += ' is_professional'
        }
        else if (authStore.authUser.isTrainee) {
            classes += ' is_trainee'
        }

    }
    else {
        classes += ' is_guest'
    }
    return classes
})

defineProps({
    loading: Boolean,
})
</script>

<template>
    <div v-if="loading" class="text-center w-screen h-full flex justify-content-center">
        <HiveLoadingIndicator :size="120"/>
    </div>
    <div :class="main_classes" v-else>
        <div class="header-bar flex align-items-center flex-nowrap background-secondary">
            <div>
                <a href="https://giep.nc/poles/spot/" class="block" rel="noreferrer noopener" target="_blank">
                    <GIEPLogo class="h-3rem block"/>
                </a>
            </div>
            <div class="text-white flex align-items-center justify-content-between w-full">
                <div class="font-family-tradegothic font-bold text-3xl ml-3 mt-1">SPOT</div>
                <div class="mr-2 text-sm flex align-items-center gap-2">
                    <AppStatus ref="appStatus"/><span v-if="appStatus?.hasContent && authStore.authUser && !authStore.authUser.isTrainee"> - </span><UserType />
                </div>
            </div>
        </div>
        <div class="flex-grow-1 flex flex-column md:flex-row overflow-hidden">

            <main class="overflow-auto flex-grow-1 md:flex-order-2" id="mainContainer"
                  style="transform: translateZ(0);">
                <slot :key="reloadKey"/>
            </main>
            <div class="md:flex-order-1">
                <TraineeNavigation v-if="authStore.authUser && authStore.authUser.isTrainee"/>
                <ProfessionalNavigation v-else-if="authStore.authUser && authStore.authUser.isProfessional"/>
                <AdminNavigation
                    v-else-if="authStore.authUser && (authStore.authUser.isAdmin || authStore.authUser.isSuperAdmin)"/>
                <GuestNavigation v-else/>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.header-bar {
    position: relative;
}

.header-bar:after {
    content: '\00a0';
    background: linear-gradient(0deg, transparent 50%, rgba(0, 0, 0, .20)) bottom;
    background-size: 100% 15px;
    background-repeat: no-repeat;
    width: 100%;
    position: absolute;
    bottom: -15px;
}
</style>
<style lang="scss">

body {
    /*background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' style='isolation:isolate' viewBox='785.75 314.931 447.5 496.138' width='447.5pt' height='496.138pt'%3E%3Cg style='opacity:0.35;'%3E%3Cpath d=' M 883.746 494.889 L 932.742 523.179 L 981.738 551.467 L 981.738 608.044 L 981.738 664.622 L 932.742 692.909 L 883.746 721.2 L 834.746 692.909 L 785.75 664.622 L 785.75 608.044 L 785.75 551.467 L 834.746 523.179 L 883.746 494.889 Z ' fill-rule='evenodd' fill='rgb(248,190,59)'/%3E%3Cpath d=' M 984.839 314.931 L 1033.836 343.221 L 1082.835 371.509 L 1082.835 428.086 L 1082.835 484.664 L 1033.836 512.951 L 984.839 541.239 L 935.843 512.951 L 886.844 484.664 L 886.844 428.086 L 886.844 371.509 L 935.843 343.221 L 984.839 314.931 Z ' fill-rule='evenodd' fill='rgb(248,190,59)'/%3E%3Cpath d=' M 1179.728 687.469 L 1206.489 702.921 L 1233.25 718.37 L 1233.25 749.27 L 1233.25 780.168 L 1206.489 795.62 L 1179.728 811.069 L 1152.971 795.62 L 1126.21 780.168 L 1126.21 749.27 L 1126.21 718.37 L 1152.971 702.921 L 1179.728 687.469 Z ' fill-rule='evenodd' fill='rgb(248,190,59)'/%3E%3Cpath d=' M 1163.301 370.639 L 1195.337 389.134 L 1227.373 407.63 L 1227.373 444.625 L 1227.373 481.617 L 1195.337 500.113 L 1163.301 518.609 L 1131.262 500.113 L 1099.226 481.617 L 1099.226 444.625 L 1099.226 407.63 L 1131.262 389.134 L 1163.301 370.639 Z ' fill-rule='evenodd' fill='rgb(248,190,59)'/%3E%3Cpath d=' M 984.839 362.476 L 1013.25 378.879 L 1041.661 395.28 L 1041.661 428.086 L 1041.661 460.89 L 1013.25 477.293 L 984.839 493.697 L 956.428 477.293 L 928.021 460.89 L 928.021 428.086 L 928.021 395.28 L 956.428 378.879 L 984.839 362.476 Z ' fill-rule='evenodd' fill='rgb(255,206,75)'/%3E%3Cpath d=' M 883.746 542.434 L 912.153 558.837 L 940.564 575.241 L 940.564 608.044 L 940.564 640.848 L 912.153 657.252 L 883.746 673.655 L 855.335 657.252 L 826.924 640.848 L 826.924 608.044 L 826.924 575.241 L 855.335 558.837 L 883.746 542.434 Z ' fill-rule='evenodd' fill='rgb(255,206,75)'/%3E%3Cpath d=' M 1162.542 401.428 L 1182.203 412.779 L 1201.864 424.13 L 1201.864 446.832 L 1201.864 469.534 L 1182.203 480.882 L 1162.542 492.233 L 1142.884 480.882 L 1123.223 469.534 L 1123.223 446.832 L 1123.223 424.13 L 1142.884 412.779 L 1162.542 401.428 Z ' fill-rule='evenodd' fill='rgb(255,206,75)'/%3E%3Cpath d=' M 1180.77 717.542 L 1195.514 726.056 L 1210.262 734.568 L 1210.262 751.595 L 1210.262 768.621 L 1195.514 777.133 L 1180.77 785.645 L 1166.026 777.133 L 1151.281 768.621 L 1151.281 751.595 L 1151.281 734.568 L 1166.026 726.056 L 1180.77 717.542 Z ' fill-rule='evenodd' fill='rgb(255,206,75)'/%3E%3Cpath d=' M 984.839 389.153 L 1001.697 398.887 L 1018.558 408.621 L 1018.558 428.086 L 1018.558 447.552 L 1001.697 457.286 L 984.839 467.02 L 967.981 457.286 L 951.123 447.552 L 951.123 428.086 L 951.123 408.621 L 967.981 398.887 L 984.839 389.153 Z ' fill-rule='evenodd' fill='rgb(252,226,166)'/%3E%3Cpath d=' M 883.746 569.111 L 900.604 578.845 L 917.462 588.579 L 917.462 608.044 L 917.462 627.513 L 900.604 637.244 L 883.746 646.978 L 866.885 637.244 L 850.027 627.513 L 850.027 608.044 L 850.027 588.579 L 866.885 578.845 L 883.746 569.111 Z ' fill-rule='evenodd' fill='rgb(252,226,166)'/%3E%3Cg%3E%3Cpath d=' M 1092.882 494.889 L 1141.878 523.179 L 1190.875 551.467 L 1190.875 608.044 L 1190.875 664.622 L 1141.878 692.909 L 1092.882 721.2 L 1043.883 692.909 L 994.887 664.622 L 994.887 608.044 L 994.887 551.467 L 1043.883 523.179 L 1092.882 494.889 Z ' fill-rule='evenodd' fill='rgb(248,190,59)'/%3E%3Cpath d=' M 1092.882 542.434 L 1121.29 558.837 L 1149.701 575.241 L 1149.701 608.044 L 1149.701 640.848 L 1121.29 657.252 L 1092.882 673.655 L 1064.471 657.252 L 1036.061 640.848 L 1036.061 608.044 L 1036.061 575.241 L 1064.471 558.837 L 1092.882 542.434 Z ' fill-rule='evenodd' fill='rgb(255,206,75)'/%3E%3Cpath d=' M 1092.882 569.111 L 1109.74 578.845 L 1126.598 588.579 L 1126.598 608.044 L 1126.598 627.513 L 1109.74 637.244 L 1092.882 646.978 L 1076.021 637.244 L 1059.163 627.513 L 1059.163 608.044 L 1059.163 588.579 L 1076.021 578.845 L 1092.882 569.111 Z ' fill-rule='evenodd' fill='rgb(252,226,166)'/%3E%3C/g%3E%3Cpath d=' M 1181.788 731.479 L 1190.875 736.724 L 1199.959 741.972 L 1199.959 752.462 L 1199.959 762.955 L 1190.875 768.203 L 1181.788 773.448 L 1172.701 768.203 L 1163.614 762.955 L 1163.614 752.462 L 1163.614 741.972 L 1172.701 736.724 L 1181.788 731.479 Z ' fill-rule='evenodd' fill='rgb(252,226,166)'/%3E%3Cpath d=' M 1162.542 422.152 L 1173.231 428.321 L 1183.916 434.491 L 1183.916 446.832 L 1183.916 459.171 L 1173.231 465.343 L 1162.542 471.513 L 1151.857 465.343 L 1141.168 459.171 L 1141.168 446.832 L 1141.168 434.491 L 1151.857 428.321 L 1162.542 422.152 Z ' fill-rule='evenodd' fill='rgb(252,226,166)'/%3E%3C/g%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: 140% -120%;*/
}
</style>
